/**
 * MyCopro - Syndic Store
 * Store Zustand pour l'espace Syndic
 * ✅ Avec fonctions addDocument, addEvent, updateEvent, deleteEvent, deleteDocument
 * ✅ CORRIGÉ: updateRequestStatus utilise "text" et authorId réel
 * ✅ CORRIGÉ: getRequestById avec fallbacks pour photos (photos/images/photoUrls)
 * ✅ NOUVEAU: updateRequestStatus envoie un email au demandeur + notification interne
 *    lors d'un changement de statut par le syndic (resolved → email clôture, rejected → email refus)
 * ✅ NOUVEAU: SyndicRequest inclut submitterEmail, submitterCategory, submitterIdentifier
 */

import { create } from 'zustand';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  updateDoc,
  addDoc,
  deleteDoc,
  Timestamp,
  limit
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { db, storage } from '@/config/firebase';
// ✅ NOUVEAU: Import du service email pour notifier le demandeur lors des changements de statut
import {
  sendRequestClosedEmail,
  sendRequestRejectedEmail,
  type RequestEmailData
} from '@/services/emailService';
import { logger } from '@/utils/logger';

// ============================================================================
// TYPES
// ============================================================================

export interface SyndicResidence {
  id: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  residenceCode: string;
  syndicReference?: string;
  stats: {
    apartments: number;
    owners: number;
    tenants: number;
  };
  pendingRequestsCount?: number;
  createdAt?: string;
}

export interface SyndicRequest {
  id: string;
  residenceId: string;
  residenceName?: string;
  type: 'commune' | 'privative' | 'problem' | 'label' | 'fiber';
  problemType?: 'collectif' | 'privatif' | 'collective' | 'private';  // ✅ CORRIGÉ: Accepte les deux formes
  category: string;
  title: string;
  description: string;
  status: 'pending' | 'validated' | 'transmitted' | 'in_progress' | 'resolved' | 'rejected' | 'closed';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt?: string;
  apartment?: string;
  photos?: string[];
  photoURL?: string;
  commentsCount?: number;
  // ✅ NOUVEAU: Complément du Conseil Syndical
  csComment?: string;
  csPhotoURLs?: string[];
  // ✅ NOUVEAU: PDF étiquette
  labelPdfUrl?: string;
  // ✅ NOUVEAU: Données étiquette BAL
  labelData?: { nom1: string; nom2?: string; numBAL?: string };
  submitterName?: string;
  submitterApartment?: string;
  building?: string;
  // ✅ NOUVEAU: Champs nécessaires pour l'envoi d'emails au demandeur
  submitterEmail?: string;
  submitterCategory?: string;
  submitterIdentifier?: string;
  ticketNumber?: string;
}

export interface SyndicDocument {
  id: string;
  residenceId: string;
  residenceName?: string;
  name: string;
  type: string;
  url: string;
  size?: number;
  uploadedBy: string;
  uploadedByName?: string;
  uploadedAt: string;
  category?: string;
  audience?: DocumentAudience;
  storagePath?: string;
}

export interface SyndicEvent {
  id: string;
  residenceId: string;
  residenceName?: string;
  title: string;
  description?: string;
  date: string;
  time?: string;
  startTime?: string;
  endTime?: string;
  location?: string;
  type: EventType;
  restrictedTo?: EventVisibility;
  createdBy: string;
  createdByName?: string;
}

export interface SyndicStats {
  totalResidences: number;
  totalLots: number;
  pendingRequests: number;
  resolvedThisMonth: number;
  upcomingEvents: number;
}

// Types pour l'ajout de documents
export type DocumentCategory = 'pv' | 'reglement' | 'travaux' | 'contrat' | 'facture' | 'autre';
export type DocumentAudience = 'all' | 'owners' | 'council';

export interface DocumentUploadData {
  title: string;
  description?: string;
  category: DocumentCategory;
  audience: DocumentAudience;
  uploadedBy: string;
  uploadedByName: string;
}

// Types pour les événements
export type EventType = 'ag' | 'travaux' | 'intervention' | 'reunion' | 'autre';
export type EventVisibility = 'all' | 'owners' | 'council';

export interface EventCreateData {
  title: string;
  description?: string;
  date: string;
  time?: string;
  startTime?: string;
  endTime?: string;
  location?: string;
  type: EventType;
  restrictedTo?: EventVisibility;
  createdBy: string;
  createdByName: string;
}

export interface EventUpdateData {
  title?: string;
  description?: string;
  date?: string;
  time?: string;
  startTime?: string;
  endTime?: string;
  location?: string;
  type?: EventType;
  restrictedTo?: EventVisibility;
}

// ✅ Type pour les infos auteur (utilisé par updateRequestStatus)
export interface AuthorInfo {
  id: string;
  name: string;
}

// ============================================================================
// STORE
// ============================================================================

interface SyndicStore {
  // Data
  syndicInfo: {
    id: string;
    companyName: string;
    contactName: string;
  } | null;
  residences: SyndicResidence[];
  requests: SyndicRequest[];
  documents: SyndicDocument[];
  events: SyndicEvent[];
  stats: SyndicStats;

  // Loading states
  isLoadingResidences: boolean;
  isLoadingRequests: boolean;
  isLoadingDocuments: boolean;
  isLoadingEvents: boolean;
  isLoadingStats: boolean;
  isUploading: boolean;

  // Actions
  setSyndicInfo: (info: { id: string; companyName: string; contactName: string }) => void;
  loadManagedResidences: (syndicId: string) => Promise<void>;
  loadAllRequests: (syndicId: string) => Promise<void>;
  loadResidenceRequests: (residenceId: string) => Promise<void>;
  loadAllDocuments: (syndicId: string) => Promise<void>;
  loadResidenceDocuments: (residenceId: string) => Promise<void>;
  loadAllEvents: (syndicId: string) => Promise<void>;
  loadResidenceEvents: (residenceId: string) => Promise<void>;
  loadStats: (syndicId: string) => Promise<void>;
  
  // Request actions
  getRequestById: (residenceId: string, requestId: string) => Promise<SyndicRequest | null>;
  // ✅ Signature mise à jour avec authorInfo
  updateRequestStatus: (
    residenceId: string, 
    requestId: string, 
    status: SyndicRequest['status'], 
    comment?: string,
    authorInfo?: AuthorInfo
  ) => Promise<boolean>;
  
  // Document actions
  addDocument: (residenceId: string, file: File, metadata: DocumentUploadData) => Promise<{ success: boolean; error?: string }>;
  deleteDocument: (residenceId: string, documentId: string) => Promise<{ success: boolean; error?: string }>;
  
  // Event actions
  addEvent: (residenceId: string, data: EventCreateData) => Promise<{ success: boolean; error?: string }>;
  updateEvent: (residenceId: string, eventId: string, data: EventUpdateData) => Promise<{ success: boolean; error?: string }>;
  deleteEvent: (residenceId: string, eventId: string) => Promise<{ success: boolean; error?: string }>;
  
  // Reset
  reset: () => void;
}

const initialState = {
  syndicInfo: null,
  residences: [],
  requests: [],
  documents: [],
  events: [],
  stats: {
    totalResidences: 0,
    totalLots: 0,
    pendingRequests: 0,
    resolvedThisMonth: 0,
    upcomingEvents: 0
  },
  isLoadingResidences: false,
  isLoadingRequests: false,
  isLoadingDocuments: false,
  isLoadingEvents: false,
  isLoadingStats: false,
  isUploading: false,
};

// Helpers
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const sanitizeFileName = (fileName: string): string => {
  const parts = fileName.split('.');
  const extension = parts.pop() || '';
  const baseName = parts.join('.');
  
  const sanitized = baseName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/_+/g, '_')
    .substring(0, 100);
  
  return `${sanitized}.${extension.toLowerCase()}`;
};

// ✅ Helper pour extraire les photos avec fallbacks
const extractPhotos = (data: Record<string, any>): string[] => {
  // Essayer plusieurs noms de champs possibles
  if (Array.isArray(data.photos) && data.photos.length > 0) {
    return data.photos;
  }
  if (Array.isArray(data.images) && data.images.length > 0) {
    return data.images;
  }
  // ✅ photoURL - le champ utilisé dans Firestore
  if (Array.isArray(data.photoURL) && data.photoURL.length > 0) {
    return data.photoURL;
  }
  // ✅ photoURL peut être une string unique
  if (typeof data.photoURL === 'string' && data.photoURL.length > 0) {
    return [data.photoURL];
  }
  if (Array.isArray(data.photoUrls) && data.photoUrls.length > 0) {
    return data.photoUrls;
  }
  if (Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
    return data.imageUrls;
  }
  if (Array.isArray(data.attachments) && data.attachments.length > 0) {
    // Filtrer uniquement les images si attachments contient des objets
    return data.attachments
      .filter((att: any) => typeof att === 'string' || att?.url)
      .map((att: any) => typeof att === 'string' ? att : att.url);
  }
  return [];
};

export const useSyndicStore = create<SyndicStore>((set, get) => ({
  ...initialState,

  // ============================================================================
  // SET SYNDIC INFO
  // ============================================================================
  
  setSyndicInfo: (info) => {
    set({ syndicInfo: info });
  },

  // ============================================================================
  // LOAD MANAGED RESIDENCES
  // ============================================================================
  
  loadManagedResidences: async (syndicId: string) => {
    set({ isLoadingResidences: true });

    try {
      const residencesQuery = query(
        collection(db, 'residences'),
        where('syndicId', '==', syndicId),
        orderBy('name', 'asc')
      );

      const snapshot = await getDocs(residencesQuery);
      const residences: SyndicResidence[] = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        
        // Charger les demandes pour compter celles visibles par le syndic
        const requestsQuery = query(
          collection(db, `residences/${docSnap.id}/requests`),
          where('status', 'in', ['pending', 'transmitted', 'in_progress', 'resolved', 'rejected'])
        );
        const requestsSnap = await getDocs(requestsQuery);
        
        // Filtrer côté client pour ne compter que les demandes syndic
        // ✅ CORRIGÉ: Les demandes privatives arrivent avec status 'transmitted'
        //    (bypass du CS), il faut donc inclure 'transmitted' dans les deux branches.
        const syndicRequests = requestsSnap.docs.filter(reqDoc => {
          const reqData = reqDoc.data();
          const isPrivateProblem = reqData.type === 'problem' && (reqData.problemType === 'privatif' || reqData.problemType === 'private');
          
          if (isPrivateProblem) {
            // Problèmes privatifs : arrivent directement en 'transmitted' (bypass CS)
            return ['transmitted', 'in_progress', 'resolved', 'rejected'].includes(reqData.status);
          } else {
            // Autres demandes : transmises par le CS ou en cours de traitement
            return ['transmitted', 'in_progress', 'resolved', 'rejected'].includes(reqData.status);
          }
        });

        residences.push({
          id: docSnap.id,
          name: data.name || '',
          address: data.address || '',
          postalCode: data.postalCode || '',
          city: data.city || '',
          residenceCode: data.residenceCode || '',
          syndicReference: data.syndicReference || '',
          stats: data.stats || { apartments: 0, owners: 0, tenants: 0 },
          pendingRequestsCount: syndicRequests.length,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || ''
        });
      }

      set({ residences, isLoadingResidences: false });
    } catch (error) {
      console.error('Error loading managed residences:', error);
      set({ isLoadingResidences: false });
    }
  },

  // ============================================================================
  // LOAD REQUESTS
  // ============================================================================
  
  loadAllRequests: async (syndicId: string) => {
    set({ isLoadingRequests: true });

    try {
      const { residences } = get();
      
      if (residences.length === 0) {
        await get().loadManagedResidences(syndicId);
      }

      const currentResidences = get().residences;
      const allRequests: SyndicRequest[] = [];

      for (const residence of currentResidences) {
        const requestsQuery = query(
          collection(db, `residences/${residence.id}/requests`),
          orderBy('createdAt', 'desc'),
          limit(50)
        );

        const snapshot = await getDocs(requestsQuery);

        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          allRequests.push({
            id: docSnap.id,
            residenceId: residence.id,
            residenceName: residence.name,
            type: data.type || 'commune',
            problemType: data.problemType,  // ✅ Ajouté
            category: data.category || '',
            title: data.title || '',
            description: data.description || '',
            status: data.status || 'pending',
            priority: data.priority,
            createdBy: data.createdBy || data.submittedBy || '',
            createdByName: data.createdByName || data.submitterName || '',
            createdAt: data.createdAt?.toDate?.()?.toISOString() || '',
            updatedAt: data.updatedAt?.toDate?.()?.toISOString(),
            apartment: data.apartment || data.submitterApartment,
            photos: extractPhotos(data),
            photoURL: data.photoURL || data.photoUrl || undefined,
            commentsCount: data.commentsCount || 0,
            // ✅ NOUVEAU: Complément CS
            csComment: data.csComment || undefined,
            csPhotoURLs: data.csPhotoURLs || undefined,
            labelPdfUrl: data.labelPdfUrl || undefined,
            labelData: data.labelData || undefined,
            submitterName: data.submitterName || undefined,
            submitterApartment: data.submitterApartment || undefined,
            // ✅ NOUVEAU: Lire l'email et la catégorie du demandeur pour l'envoi d'emails
            submitterEmail: data.submitterEmail || undefined,
            submitterCategory: data.submitterCategory || undefined,
            submitterIdentifier: data.submitterIdentifier || undefined,
            ticketNumber: data.ticketNumber || undefined,
          });
        });
      }

      allRequests.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      // ✅ FILTRAGE SYNDIC (CORRIGÉ): 
      // - Problèmes privatifs : arrivent directement en 'transmitted' (bypass CS)
      // - Autres demandes : transmises par le CS ou en cours de traitement par le syndic
      // Dans les deux cas, le syndic voit les demandes à partir du statut 'transmitted'.
      const filteredRequests = allRequests.filter(r => {
        const isPrivateProblem = r.type === 'problem' && (r.problemType === 'privatif' || r.problemType === 'private');
        
        if (isPrivateProblem) {
          // Problèmes privatifs : arrivent directement en 'transmitted' (bypass CS)
          return ['transmitted', 'in_progress', 'resolved', 'rejected'].includes(r.status);
        } else {
          // Autres demandes : transmises par le CS ou en cours/résolues/refusées par le syndic
          return ['transmitted', 'in_progress', 'resolved', 'rejected'].includes(r.status);
        }
      });

      set({ requests: filteredRequests, isLoadingRequests: false });
    } catch (error) {
      console.error('Error loading requests:', error);
      set({ isLoadingRequests: false });
    }
  },

  loadResidenceRequests: async (residenceId: string) => {
    set({ isLoadingRequests: true });

    try {
      const requestsQuery = query(
        collection(db, `residences/${residenceId}/requests`),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(requestsQuery);
      const residenceDoc = await getDoc(doc(db, 'residences', residenceId));
      const residenceName = residenceDoc.data()?.name || '';

      const requests: SyndicRequest[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          residenceId,
          residenceName,
          type: data.type || 'commune',
          problemType: data.problemType,
          category: data.category || '',
          title: data.title || '',
          description: data.description || '',
          status: data.status || 'pending',
          priority: data.priority,
          createdBy: data.createdBy || data.submittedBy || '',
          createdByName: data.createdByName || data.submitterName || '',
          createdAt: data.createdAt?.toDate?.()?.toISOString() || '',
          updatedAt: data.updatedAt?.toDate?.()?.toISOString(),
          apartment: data.apartment || data.submitterApartment,
          photos: extractPhotos(data),
          photoURL: data.photoURL || data.photoUrl || undefined,
          commentsCount: data.commentsCount || 0,
          // ✅ NOUVEAU: Complément CS
          csComment: data.csComment || undefined,
          csPhotoURLs: data.csPhotoURLs || undefined,
          labelPdfUrl: data.labelPdfUrl || undefined,
          labelData: data.labelData || undefined,
          submitterName: data.submitterName || undefined,
          submitterApartment: data.submitterApartment || undefined,
          // ✅ NOUVEAU: Lire l'email et la catégorie du demandeur pour l'envoi d'emails
          submitterEmail: data.submitterEmail || undefined,
          submitterCategory: data.submitterCategory || undefined,
          submitterIdentifier: data.submitterIdentifier || undefined
        };
      });

      // ✅ FILTRAGE SYNDIC (CORRIGÉ): même logique que loadAllRequests
      // Les demandes privatives arrivent en 'transmitted' (bypass CS)
      const filteredRequests = requests.filter(r => {
        const isPrivateProblem = r.type === 'problem' && (r.problemType === 'privatif' || r.problemType === 'private');
        
        if (isPrivateProblem) {
          // Problèmes privatifs : arrivent directement en 'transmitted' (bypass CS)
          return ['transmitted', 'in_progress', 'resolved', 'rejected'].includes(r.status);
        } else {
          // Autres demandes : transmises par le CS ou en cours de traitement
          return ['transmitted', 'in_progress', 'resolved', 'rejected'].includes(r.status);
        }
      });

      set({ requests: filteredRequests, isLoadingRequests: false });
    } catch (error) {
      console.error('Error loading residence requests:', error);
      set({ isLoadingRequests: false });
    }
  },

  // ============================================================================
  // REQUEST ACTIONS
  // ============================================================================

  getRequestById: async (residenceId: string, requestId: string) => {
    try {
      const docRef = doc(db, `residences/${residenceId}/requests`, requestId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) return null;

      const data = docSnap.data();
      const residenceDoc = await getDoc(doc(db, 'residences', residenceId));
      
      return {
        id: docSnap.id,
        residenceId,
        residenceName: residenceDoc.data()?.name || '',
        type: data.type || 'commune',
        problemType: data.problemType,  // ✅ Ajouté
        category: data.category || '',
        title: data.title || '',
        description: data.description || '',
        status: data.status || 'pending',
        priority: data.priority,
        createdBy: data.createdBy || data.submittedBy || '',
        createdByName: data.createdByName || data.submitterName || '',
        createdAt: data.createdAt?.toDate?.()?.toISOString() || '',
        updatedAt: data.updatedAt?.toDate?.()?.toISOString(),
        apartment: data.apartment || data.submitterApartment,
        // ✅ Utiliser le helper avec fallbacks
        photos: extractPhotos(data),
        photoURL: data.photoURL || data.photoUrl || undefined,
        commentsCount: data.commentsCount || 0,
        // ✅ NOUVEAU: Complément CS
        csComment: data.csComment || undefined,
        csPhotoURLs: data.csPhotoURLs || undefined,
        labelPdfUrl: data.labelPdfUrl || undefined,
        labelData: data.labelData || undefined,
        submitterName: data.submitterName || undefined,
        submitterApartment: data.submitterApartment || undefined,
        // ✅ NOUVEAU: Lire l'email et la catégorie du demandeur pour l'envoi d'emails
        submitterEmail: data.submitterEmail || undefined,
        submitterCategory: data.submitterCategory || undefined,
        submitterIdentifier: data.submitterIdentifier || undefined,
        building: data.building || undefined,
        ticketNumber: data.ticketNumber || undefined,
      };
    } catch (error) {
      console.error('Error getting request:', error);
      return null;
    }
  },

  // ✅ CORRIGÉ: utilise "text" au lieu de "content", et authorId réel
  // ✅ NOUVEAU: Envoie un email au demandeur + notification interne
  //    lors d'un changement de statut par le syndic (resolved, rejected, in_progress)
  updateRequestStatus: async (
    residenceId: string, 
    requestId: string, 
    status: SyndicRequest['status'], 
    comment?: string,
    authorInfo?: AuthorInfo
  ) => {
    try {
      const docRef = doc(db, `residences/${residenceId}/requests`, requestId);
      
      await updateDoc(docRef, {
        status,
        updatedAt: Timestamp.now()
      });

      // ✅ Ajouter le commentaire avec les bons champs
      if (comment && authorInfo) {
        await addDoc(collection(db, `residences/${residenceId}/requests/${requestId}/comments`), {
          text: comment,
          authorId: authorInfo.id,
          authorName: authorInfo.name,
          authorCategory: 'syndic',  // ✅ Utiliser authorCategory (cohérent avec ValidationsPage)
          createdAt: Timestamp.now()
        });
      }

      // ✅ Mettre à jour le state local
      set((state) => ({
        requests: state.requests.map((r) =>
          r.id === requestId ? { ...r, status, updatedAt: new Date().toISOString() } : r
        )
      }));

      // =====================================================================
      // ✅ NOUVEAU: Envoi d'email + notification interne au demandeur
      // Déclenché pour les statuts terminaux (resolved, rejected)
      // Le bloc est dans un try/catch séparé pour ne pas bloquer le retour
      // si l'envoi d'email échoue (le changement de statut est déjà persisté)
      // =====================================================================
      try {
        // 1. Lire les données complètes de la demande (pour submitterEmail, etc.)
        const requestSnap = await getDoc(docRef);
        const requestData = requestSnap.data();
        if (!requestData) {
          logger.db(' Request data not found after update, skipping email');
          return true;
        }

        const submitterEmail = requestData.submitterEmail || '';
        const submittedBy = requestData.submittedBy || requestData.createdBy || '';
        // Pas d'email stocké → on ne peut pas envoyer
        if (!submitterEmail) {
          logger.db(' No submitterEmail found, skipping email for request', requestId);
          return true;
        }

        // 2. Lire les infos résidence (nom, adresse, syndicReference, residenceCode)
        const residenceSnap = await getDoc(doc(db, 'residences', residenceId));
        const residenceData = residenceSnap.data() || {};

        // 3. Construire les données email (même structure que useRequests.buildEmailData)
        const emailData: RequestEmailData = {
          requestId,
          requestType: requestData.type || 'problem',
          requestTitle: requestData.title || '',
          requestDescription: requestData.description || '',
          problemType: requestData.problemType,
          photoURL: requestData.photoURL || requestData.photoUrl,
          // Infos demandeur
          submitterName: requestData.submitterName || requestData.createdByName || 'Résident',
          submitterEmail,
          submitterCategory: requestData.submitterCategory || 'tenant',
          submitterIdentifier: requestData.submitterIdentifier || '',
          submitterApartment: requestData.submitterApartment || requestData.apartment || '',
          // Infos résidence
          residenceName: residenceData.name || '',
          residenceAddress: residenceData.address
            ? `${residenceData.address}, ${residenceData.postalCode || ''} ${residenceData.city || ''}`.trim()
            : '',
          residenceCode: residenceData.residenceCode || '',
          syndicReference: residenceData.syndicReference || '',
          // Infos action
          actionBy: authorInfo?.name || 'Le syndic',
          actionReason: comment || undefined
        };

        // 4. Déterminer le CC pour le conseil syndical (demandes collectives uniquement)
        // ✅ NOUVEAU: Les demandes privatives ne doivent PAS être envoyées en copie au CS (confidentialité)
        const isPrivateProblem = requestData.type === 'problem' 
          && (requestData.problemType === 'private' || requestData.problemType === 'privatif');
        const csEmail = !isPrivateProblem ? (residenceData.csEmail || null) : null;
        const ccList: string[] = [];
        if (csEmail && csEmail !== submitterEmail) {
          ccList.push(csEmail);
        }

        // 5. Envoyer l'email selon le nouveau statut
        if (status === 'resolved') {
          // ✅ "Résolu" par le syndic = équivalent de "clôturé" pour le demandeur
          // ✅ CC au conseil syndical si demande collective et csEmail renseigné
          await sendRequestClosedEmail(emailData, ccList.length > 0 ? ccList : undefined);
          logger.db(' Email de clôture envoyé à:', submitterEmail, ccList.length > 0 ? '(CC: ' + ccList.join(', ') + ')' : '');
        } else if (status === 'rejected') {
          // ✅ Refus par le syndic
          // ✅ CC au conseil syndical si demande collective et csEmail renseigné
          await sendRequestRejectedEmail(emailData, ccList.length > 0 ? ccList : undefined);
          logger.db(' Email de refus envoyé à:', submitterEmail, ccList.length > 0 ? '(CC: ' + ccList.join(', ') + ')' : '');
        }
        // Note: pas d'email pour 'in_progress' (transition intermédiaire)

        // 6. Créer une notification interne dans Firestore pour le demandeur
        const notifTitle = status === 'resolved' 
          ? '✔️ Demande traitée par le syndic'
          : status === 'rejected'
            ? '❌ Demande refusée par le syndic'
            : status === 'in_progress'
              ? '🔧 Demande prise en charge par le syndic'
              : '📋 Statut de votre demande mis à jour';

        const notifMessage = status === 'resolved'
          ? `Votre demande "${requestData.title}" a été traitée.${comment ? ' Commentaire : ' + comment : ''}`
          : status === 'rejected'
            ? `Votre demande "${requestData.title}" a été refusée.${comment ? ' Motif : ' + comment : ''}`
            : status === 'in_progress'
              ? `Votre demande "${requestData.title}" est en cours de traitement par le syndic.`
              : `Le statut de votre demande "${requestData.title}" a été mis à jour.`;

        const notifType = status === 'resolved' ? 'success'
          : status === 'rejected' ? 'error'
          : 'info';

        await addDoc(collection(db, `residences/${residenceId}/notifications`), {
          title: notifTitle,
          message: notifMessage,
          type: notifType,
          targetUserId: submittedBy,
          sentBy: authorInfo?.id || 'syndic',
          sentByName: authorInfo?.name || 'Le syndic',
          link: '/requests/' + requestId,
          readBy: [],
          createdAt: Timestamp.now()
        });
        logger.db(' Notification interne envoyée à:', submittedBy);

      } catch (emailError) {
        // ✅ L'email/notification échoue silencieusement — le changement de statut est déjà persisté
        console.error('[syndicStore] Erreur envoi email/notification (non bloquant):', emailError);
      }

      return true;
    } catch (error) {
      console.error('Error updating request status:', error);
      return false;
    }
  },

  // ============================================================================
  // LOAD DOCUMENTS
  // ============================================================================
  
  loadAllDocuments: async (syndicId: string) => {
    set({ isLoadingDocuments: true });

    try {
      const { residences } = get();
      
      if (residences.length === 0) {
        await get().loadManagedResidences(syndicId);
      }

      const currentResidences = get().residences;
      const allDocuments: SyndicDocument[] = [];

      for (const residence of currentResidences) {
        const docsQuery = query(
          collection(db, `residences/${residence.id}/documents`),
          orderBy('uploadedAt', 'desc'),
          limit(20)
        );

        const snapshot = await getDocs(docsQuery);

        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          allDocuments.push({
            id: docSnap.id,
            residenceId: residence.id,
            residenceName: residence.name,
            name: data.title || data.name || data.fileName || '',
            type: data.fileType || data.type || '',
            url: data.fileUrl || data.url || '',
            size: data.fileSizeBytes || data.size,
            uploadedBy: data.uploadedBy || '',
            uploadedByName: data.uploadedByName || '',
            uploadedAt: data.uploadedAt?.toDate?.()?.toISOString() || '',
            category: data.category,
            audience: data.audience,
            storagePath: data.storagePath
          });
        });
      }

      allDocuments.sort((a, b) => 
        new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
      );

      set({ documents: allDocuments, isLoadingDocuments: false });
    } catch (error) {
      console.error('Error loading documents:', error);
      set({ isLoadingDocuments: false });
    }
  },

  loadResidenceDocuments: async (residenceId: string) => {
    set({ isLoadingDocuments: true });

    try {
      const docsQuery = query(
        collection(db, `residences/${residenceId}/documents`),
        orderBy('uploadedAt', 'desc')
      );

      const snapshot = await getDocs(docsQuery);
      const residenceDoc = await getDoc(doc(db, 'residences', residenceId));
      const residenceName = residenceDoc.data()?.name || '';

      const documents: SyndicDocument[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          residenceId,
          residenceName,
          name: data.title || data.name || data.fileName || '',
          type: data.fileType || data.type || '',
          url: data.fileUrl || data.url || '',
          size: data.fileSizeBytes || data.size,
          uploadedBy: data.uploadedBy || '',
          uploadedByName: data.uploadedByName || '',
          uploadedAt: data.uploadedAt?.toDate?.()?.toISOString() || '',
          category: data.category,
          audience: data.audience,
          storagePath: data.storagePath
        };
      });

      set({ documents, isLoadingDocuments: false });
    } catch (error) {
      console.error('Error loading residence documents:', error);
      set({ isLoadingDocuments: false });
    }
  },

  // ============================================================================
  // ADD DOCUMENT
  // ============================================================================
  
  addDocument: async (residenceId: string, file: File, metadata: DocumentUploadData) => {
    if (!residenceId) {
      return { success: false, error: 'Résidence non sélectionnée' };
    }

    // Vérifier la taille (max 10 MB)
    if (file.size > 10 * 1024 * 1024) {
      return { success: false, error: 'Fichier trop volumineux (max 10 MB)' };
    }

    set({ isUploading: true });

    try {
      // Générer un nom unique
      const sanitizedName = sanitizeFileName(file.name);
      const uniqueName = `${Date.now()}_${sanitizedName}`;
      const storagePath = `residences/${residenceId}/documents/${uniqueName}`;
      
      // Upload vers Firebase Storage
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      
      // Obtenir l'URL
      const fileUrl = await getDownloadURL(storageRef);
      
      // Créer le document dans Firestore
      const docsRef = collection(db, 'residences', residenceId, 'documents');
      await addDoc(docsRef, {
        title: metadata.title.trim(),
        name: metadata.title.trim(),
        description: metadata.description?.trim() || '',
        category: metadata.category,
        audience: metadata.audience,
        fileName: file.name,
        fileSize: formatFileSize(file.size),
        fileSizeBytes: file.size,
        fileType: file.type,
        type: file.type,
        fileUrl,
        url: fileUrl,
        storagePath,
        uploadedAt: Timestamp.now(),
        uploadedBy: metadata.uploadedBy,
        uploadedByName: metadata.uploadedByName
      });

      // Notification
      const notificationsRef = collection(db, 'residences', residenceId, 'notifications');
      
      // Déterminer restrictedTo basé sur audience
      const notificationData: Record<string, any> = {
        title: '📄 Nouveau document',
        message: `"${metadata.title}" ajouté par ${metadata.uploadedByName}`,
        type: 'info',
        createdAt: Timestamp.now(),
        sentBy: metadata.uploadedBy,
        sentByName: metadata.uploadedByName,
        readBy: [],
        link: '/documents'
      };

      if (metadata.audience === 'owners') {
        notificationData.restrictedTo = ['owner'];
      } else if (metadata.audience === 'council') {
        notificationData.restrictedTo = ['council'];
      }

      await addDoc(notificationsRef, notificationData);

      set({ isUploading: false });
      
      // Recharger les documents
      const { syndicInfo } = get();
      if (syndicInfo?.id) {
        await get().loadAllDocuments(syndicInfo.id);
      }

      return { success: true };
    } catch (error) {
      console.error('Error uploading document:', error);
      set({ isUploading: false });
      return { success: false, error: 'Erreur lors de l\'upload' };
    }
  },

  // ============================================================================
  // DELETE DOCUMENT
  // ============================================================================

  deleteDocument: async (residenceId: string, documentId: string) => {
    if (!residenceId || !documentId) {
      return { success: false, error: 'Paramètres manquants' };
    }

    try {
      // Récupérer le document pour avoir le storagePath
      const { documents } = get();
      const document = documents.find(d => d.id === documentId);
      
      // Supprimer le fichier de Storage si storagePath existe
      if (document?.storagePath) {
        try {
          const storageRef = ref(storage, document.storagePath);
          await deleteObject(storageRef);
        } catch (e) {
          logger.storage('Could not delete file from storage:', e);
          // Continuer même si le fichier n'existe pas dans Storage
        }
      }

      // Supprimer le document de Firestore
      const docRef = doc(db, 'residences', residenceId, 'documents', documentId);
      await deleteDoc(docRef);
      
      // Mettre à jour le state local
      set((state) => ({
        documents: state.documents.filter(d => d.id !== documentId)
      }));

      return { success: true };
    } catch (error) {
      console.error('Error deleting document:', error);
      return { success: false, error: 'Erreur lors de la suppression' };
    }
  },

  // ============================================================================
  // LOAD EVENTS
  // ============================================================================
  
  loadAllEvents: async (syndicId: string) => {
    set({ isLoadingEvents: true });

    try {
      const { residences } = get();
      
      if (residences.length === 0) {
        await get().loadManagedResidences(syndicId);
      }

      const currentResidences = get().residences;
      const allEvents: SyndicEvent[] = [];

      for (const residence of currentResidences) {
        const eventsQuery = query(
          collection(db, `residences/${residence.id}/events`),
          orderBy('date', 'asc'),
          limit(50)
        );

        const snapshot = await getDocs(eventsQuery);

        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          allEvents.push({
            id: docSnap.id,
            residenceId: residence.id,
            residenceName: residence.name,
            title: data.title || '',
            description: data.description,
            date: data.date?.toDate?.()?.toISOString() || '',
            time: data.time || data.startTime,
            startTime: data.startTime || data.time,
            endTime: data.endTime,
            location: data.location,
            type: data.type || 'autre',
            restrictedTo: data.restrictedTo,
            createdBy: data.createdBy || '',
            createdByName: data.createdByName
          });
        });
      }

      allEvents.sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      set({ events: allEvents, isLoadingEvents: false });
    } catch (error) {
      console.error('Error loading events:', error);
      set({ isLoadingEvents: false });
    }
  },

  loadResidenceEvents: async (residenceId: string) => {
    set({ isLoadingEvents: true });

    try {
      const eventsQuery = query(
        collection(db, `residences/${residenceId}/events`),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(eventsQuery);
      const residenceDoc = await getDoc(doc(db, 'residences', residenceId));
      const residenceName = residenceDoc.data()?.name || '';

      const events: SyndicEvent[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          residenceId,
          residenceName,
          title: data.title || '',
          description: data.description,
          date: data.date?.toDate?.()?.toISOString() || '',
          time: data.time || data.startTime,
          startTime: data.startTime || data.time,
          endTime: data.endTime,
          location: data.location,
          type: data.type || 'autre',
          restrictedTo: data.restrictedTo,
          createdBy: data.createdBy || '',
          createdByName: data.createdByName
        };
      });

      set({ events, isLoadingEvents: false });
    } catch (error) {
      console.error('Error loading residence events:', error);
      set({ isLoadingEvents: false });
    }
  },

  // ============================================================================
  // ADD EVENT
  // ============================================================================

  addEvent: async (residenceId: string, data: EventCreateData) => {
    if (!residenceId) {
      return { success: false, error: 'Résidence non sélectionnée' };
    }

    if (!data.title?.trim()) {
      return { success: false, error: 'Le titre est requis' };
    }

    if (!data.date) {
      return { success: false, error: 'La date est requise' };
    }

    try {
      const eventsRef = collection(db, 'residences', residenceId, 'events');
      
      const eventData: Record<string, any> = {
        title: data.title.trim(),
        date: Timestamp.fromDate(new Date(data.date)),
        type: data.type || 'autre',
        createdAt: Timestamp.now(),
        createdBy: data.createdBy,
        createdByName: data.createdByName
      };

      if (data.description?.trim()) eventData.description = data.description.trim();
      if (data.time) {
        eventData.time = data.time;
        eventData.startTime = data.time; // Compatibilité
      } else if (data.startTime) {
        eventData.startTime = data.startTime;
        eventData.time = data.startTime;
      }
      if (data.endTime) eventData.endTime = data.endTime;
      if (data.location?.trim()) eventData.location = data.location.trim();
      if (data.restrictedTo && data.restrictedTo !== 'all') {
        eventData.restrictedTo = data.restrictedTo;
      }

      await addDoc(eventsRef, eventData);

      // Notification
      const notificationsRef = collection(db, 'residences', residenceId, 'notifications');
      const eventDate = new Date(data.date);
      const dateStr = eventDate.toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      });

      const notificationData: Record<string, any> = {
        title: '📅 Nouvel événement',
        message: `"${data.title}" - ${dateStr}`,
        type: 'info',
        createdAt: Timestamp.now(),
        sentBy: data.createdBy,
        sentByName: data.createdByName,
        readBy: [],
        link: '/calendar'
      };

      // Ajouter restrictedTo si nécessaire
      if (data.restrictedTo === 'owners') {
        notificationData.restrictedTo = ['owner'];
      } else if (data.restrictedTo === 'council') {
        notificationData.restrictedTo = ['council'];
      }

      await addDoc(notificationsRef, notificationData);

      // Recharger les événements
      const { syndicInfo } = get();
      if (syndicInfo?.id) {
        await get().loadAllEvents(syndicInfo.id);
      }

      return { success: true };
    } catch (error) {
      console.error('Error adding event:', error);
      return { success: false, error: 'Erreur lors de la création' };
    }
  },

  // ============================================================================
  // UPDATE EVENT
  // ============================================================================

  updateEvent: async (residenceId: string, eventId: string, data: EventUpdateData) => {
    if (!residenceId || !eventId) {
      return { success: false, error: 'Paramètres manquants' };
    }

    try {
      const eventRef = doc(db, 'residences', residenceId, 'events', eventId);
      
      const updateData: Record<string, any> = {
        updatedAt: Timestamp.now()
      };

      if (data.title !== undefined) updateData.title = data.title.trim();
      if (data.description !== undefined) updateData.description = data.description?.trim() || null;
      if (data.date !== undefined) updateData.date = Timestamp.fromDate(new Date(data.date));
      if (data.time !== undefined) {
        updateData.time = data.time || null;
        updateData.startTime = data.time || null;
      } else if (data.startTime !== undefined) {
        updateData.startTime = data.startTime || null;
        updateData.time = data.startTime || null;
      }
      if (data.endTime !== undefined) updateData.endTime = data.endTime || null;
      if (data.location !== undefined) updateData.location = data.location?.trim() || null;
      if (data.type !== undefined) updateData.type = data.type;
      if (data.restrictedTo !== undefined) {
        updateData.restrictedTo = data.restrictedTo === 'all' ? null : data.restrictedTo;
      }

      await updateDoc(eventRef, updateData);

      // Mettre à jour le state local
      set((state) => ({
        events: state.events.map((e) =>
          e.id === eventId 
            ? { 
                ...e, 
                ...data,
                date: data.date ? new Date(data.date).toISOString() : e.date
              } 
            : e
        )
      }));

      return { success: true };
    } catch (error) {
      console.error('Error updating event:', error);
      return { success: false, error: 'Erreur lors de la modification' };
    }
  },

  // ============================================================================
  // DELETE EVENT
  // ============================================================================

  deleteEvent: async (residenceId: string, eventId: string) => {
    if (!residenceId || !eventId) {
      return { success: false, error: 'Paramètres manquants' };
    }

    try {
      const eventRef = doc(db, 'residences', residenceId, 'events', eventId);
      await deleteDoc(eventRef);
      
      // Mettre à jour le state local
      set((state) => ({
        events: state.events.filter(e => e.id !== eventId)
      }));

      return { success: true };
    } catch (error) {
      console.error('Error deleting event:', error);
      return { success: false, error: 'Erreur lors de la suppression' };
    }
  },

  // ============================================================================
  // LOAD STATS
  // ============================================================================
  
  loadStats: async (syndicId: string) => {
    set({ isLoadingStats: true });

    try {
      const { residences, requests, events } = get();

      if (residences.length === 0) {
        await get().loadManagedResidences(syndicId);
      }
      if (requests.length === 0) {
        await get().loadAllRequests(syndicId);
      }
      if (events.length === 0) {
        await get().loadAllEvents(syndicId);
      }

      const currentResidences = get().residences;
      const currentRequests = get().requests;
      const currentEvents = get().events;

      const totalLots = currentResidences.reduce((acc, r) => acc + (r.stats.apartments || 0), 0);
      // ✅ CORRIGÉ: Pour le syndic, les demandes "à traiter" = 'transmitted' + 'in_progress'
      // ('pending' et 'validated' sont des statuts côté CS, le syndic ne les voit pas)
      const pendingRequests = currentRequests.filter(r => 
        ['transmitted', 'in_progress'].includes(r.status)
      ).length;

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const resolvedThisMonth = currentRequests.filter(r => 
        r.status === 'resolved' && 
        r.updatedAt && 
        new Date(r.updatedAt) >= startOfMonth
      ).length;

      const upcomingEvents = currentEvents.length;

      set({
        stats: {
          totalResidences: currentResidences.length,
          totalLots,
          pendingRequests,
          resolvedThisMonth,
          upcomingEvents
        },
        isLoadingStats: false
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      set({ isLoadingStats: false });
    }
  },

  // ============================================================================
  // RESET
  // ============================================================================
  
  reset: () => {
    set(initialState);
  }
}));

export default useSyndicStore;
